/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import Loader from 'react-loader-spinner';
import ReactSelect, { createFilter } from 'react-windowed-select';

import ArrowIcon from 'apps/web_forms/assets/arrow.svg';
import { StyledLoadingIndicator } from 'apps/web_forms/components/commons/SelectMultiple/style';
import useSelectStyles from 'apps/web_forms/hooks/useSelectStyles';
import { COLORS, EMPTY_DATETIME_VALUE } from 'apps/web_forms/utils/constants';

import { CustomSelectContainer } from './styles';

const Select = React.forwardRef(
  (
    {
      name,
      id,
      onSelect,
      onClick,
      options,
      defaultValue,
      isValid,
      hasResourceUpdateHighlight,
      removeResourceUpdateHighlight,
      disabled,
      useSelectRadioButtons,
      loadingResource,
      emptyOptionText,
      parentValue,
      reactSelect,
    },
    ref
  ) => {
    const [radioButtons, setRadioButtons] = useState({});
    const [value, setValue] = useState();

    useEffect(() => {
      if (defaultValue) {
        setValue({
          label: options.find((x) => x.value === defaultValue)?.label,
          value: defaultValue,
        });
      }
    }, [defaultValue, options]);

    useEffect(() => {
      const result = {};
      options.forEach((option) => {
        const radioValue = option.value || option.label;
        result[radioValue] = radioValue === defaultValue;
      });
      setRadioButtons(result);
    }, [options]);

    const radioButtonClickHandler = (radioElement) => {
      const radioValue = radioElement.value;

      const wasChecked = radioButtons[radioValue];
      // eslint-disable-next-line no-param-reassign
      if (wasChecked) radioElement.checked = false;

      const currentValue = radioButtons[radioValue];

      Object.keys(radioButtons).forEach((item) => {
        radioButtons[item] = false;
      });
      setRadioButtons({ ...radioButtons, [radioValue]: !currentValue });
      
      if (!currentValue) {
        onSelect(radioValue);
      } else {
        onSelect('');
      }
    };

    const { errorStyles, customStyles } = useSelectStyles({
      isValid,
      hasResourceUpdateHighlight,
    });

    return (
      <CustomSelectContainer>
        {loadingResource && (
          <StyledLoadingIndicator className="select-loader">
            <span>Loading options</span>
            <Loader
              type='Oval'
              color={COLORS.primary}
              height='1.5em'
              width={15}
            />
          </StyledLoadingIndicator>
        )}

        {!useSelectRadioButtons && !loadingResource && reactSelect && (
          <ReactSelect
            aria-label={name}
            styles={customStyles}
            theme={(theme) => ({
              ...theme,
              borderRadius: 0,
              colors: {
                ...theme.colors,
                ...errorStyles,
              },
            })}
            name={name}
            id={id}
            innerRef={ref}
            onFocus={() => {
              onClick();
              removeResourceUpdateHighlight();
            }}
            value={value}
            placeholder={emptyOptionText || ''}
            isClearable
            options={options}
            onChange={(e) => {
              onSelect(e?.value ?? '');
              if (e) {
                setValue({ label: e.label, value: e.value });
              } else {
                setValue();
              }
            }}
            isDisabled={disabled}
            filterOption={createFilter({ ignoreAccents: false })}
          />
        )}

        {!useSelectRadioButtons && !loadingResource && !reactSelect && (
          <>
            <select
              name={name}
              ref={ref}
              onClick={onClick}
              onChange={(e) => {
                onSelect(e.target.value);
              }}
              onFocus={removeResourceUpdateHighlight}
              value={parentValue}
              defaultValue={defaultValue}
              id={id}
              className={clsx(
                'select-input',
                !isValid && 'invalid',
                hasResourceUpdateHighlight && 'updateHighlight'
              )}
              disabled={disabled}
              // eslint-disable-next-line react/jsx-props-no-spreading
            >
              <option value={EMPTY_DATETIME_VALUE}>{emptyOptionText || '--'}</option>
              {options.map((option) => (
                <option
                  key={option.value || option.label}
                  name={option.value || option.label}
                  value={option.value || option.label}
                >
                  {option.label}
                </option>
              ))}
            </select>
            <ArrowIcon className='arrow-icon' />
          </>
        )}

        {useSelectRadioButtons && !loadingResource && (
          <>
            <div className='boolean-container' ref={ref}>
              {options.map((option) => {
                const radioValue = option.value || option.label;
                //adding UUID as fix for DME-1120
                var randomUUID = Math.random().toString(36).slice(-6);

                return (
                  <label
                    htmlFor={`radio-${radioValue}-${randomUUID}`}
                    className='select-boolean-label'
                    key={radioValue}
                  >
                    <input
                      id={`radio-${radioValue}-${randomUUID}`}
                      className={clsx(
                        !isValid && 'invalid',
                        hasResourceUpdateHighlight && 'updateHighlight'
                      )}
                      value={radioValue}
                      type='radio'
                      disabled={disabled}
                      name={name}
                      defaultChecked={radioButtons[radioValue]}
                      onClick={(e) => {
                        radioButtonClickHandler(e.target);
                      }}
                      onFocus={removeResourceUpdateHighlight}
                    />
                    {option.label}
                  </label>
                );
              })}
            </div>
          </>
        )}
      </CustomSelectContainer>
    );
  }
);

Select.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  defaultValue: PropTypes.string,
  isValid: PropTypes.bool,
  hasResourceUpdateHighlight: PropTypes.bool,
  removeResourceUpdateHighlight: PropTypes.func,
  disabled: PropTypes.bool,
  useSelectRadioButtons: PropTypes.bool,
  loadingResource: PropTypes.bool,
  emptyOptionText: PropTypes.string,
  reactSelect: PropTypes.bool,
  parentValue: PropTypes.string,
};

Select.defaultProps = {
  isValid: true,
  hasResourceUpdateHighlight: false,
  removeResourceUpdateHighlight: () => {},
  disabled: false,
  onClick: () => {},
  reactSelect: true,
};

export default Select;
